import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  Button,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import StandardizedChip from '../common/StandardizedChip';
import {
  Security as SecurityIcon,
  Info as InfoIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Domain as DomainIcon,
  Shield as ShieldIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { apiFetch } from '../../config/api';
import { debugError } from '../../utils/debugLog';

/**
 * Entity Information Component
 *
 * Displays comprehensive information about supported entities,
 * domains, and anonymization methods for the content check system.
 */
const EntityInformation = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [entityData, setEntityData] = useState(null);

  useEffect(() => {
    fetchEntityInformation();
  }, []);

  const fetchEntityInformation = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiFetch('/api/v1/entity-info/summary');
      setEntityData(response);
    } catch (err) {
      debugError('Error fetching entity information:', err);
      setError('Failed to load entity information');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const getSensitivityColor = level => {
    switch (level) {
      case 'restricted':
        return 'error';
      case 'confidential':
        return 'warning';
      case 'public':
        return 'success';
      default:
        return 'default';
    }
  };

  const getMethodColor = method => {
    switch (method) {
      case 'hash':
        return 'error';
      case 'mask':
        return 'warning';
      case 'replace':
        return 'info';
      default:
        return 'default';
    }
  };

  const getSecurityIcon = level => {
    switch (level) {
      case 'restricted':
        return <ErrorIcon color="error" />;
      case 'confidential':
        return <WarningIcon color="warning" />;
      case 'public':
        return <CheckIcon color="success" />;
      default:
        return <InfoIcon />;
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading entity information...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
        <Button onClick={fetchEntityInformation} sx={{ ml: 2 }}>
          Retry
        </Button>
      </Alert>
    );
  }

  if (!entityData) {
    return <Alert severity="warning">No entity information available</Alert>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Entity Information
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Comprehensive information about supported entities, domains, and
        anonymization methods.
      </Typography>

      <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 3 }}>
        <Tab label="Overview" />
        <Tab label="Entities" />
        <Tab label="Domains" />
        <Tab label="Anonymization Methods" />
      </Tabs>

      {/* Overview Tab */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <DomainIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Domains</Typography>
                </Box>
                <Typography variant="h3" color="primary">
                  {entityData.total_domains}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Supported domains
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <SecurityIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Entities</Typography>
                </Box>
                <Typography variant="h3" color="primary">
                  {entityData.total_entities}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Supported entity types
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <ShieldIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Methods</Typography>
                </Box>
                <Typography variant="h3" color="primary">
                  {entityData.anonymization_methods?.length || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Anonymization methods
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Entities Tab */}
      {activeTab === 1 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Supported Entities
            </Typography>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Entity Type</TableCell>
                    <TableCell>Display Name</TableCell>
                    <TableCell align="center">
                      Entity Resolution Engine
                    </TableCell>
                    <TableCell align="center">
                      Pattern Recognition Engine
                    </TableCell>
                    <TableCell>Sensitivity</TableCell>
                    <TableCell>Anonymization Method</TableCell>
                    <TableCell>Examples</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {entityData.entities?.map((entity, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <StandardizedChip
                          label={entity.entity_type}
                          color="primary"
                          width={140}
                        />
                      </TableCell>
                      <TableCell>{entity.display_name}</TableCell>
                      <TableCell align="center">
                        {entity.engines?.includes(
                          'Entity Resolution Engine'
                        ) ? (
                          <CheckIcon color="primary" />
                        ) : (
                          <Typography variant="body2" color="text.disabled">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {entity.engines?.includes(
                          'Pattern Recognition Engine'
                        ) ? (
                          <CheckIcon color="secondary" />
                        ) : (
                          <Typography variant="body2" color="text.disabled">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          {getSecurityIcon(entity.sensitivity_level)}
                          <StandardizedChip
                            label={entity.sensitivity_level}
                            color={getSensitivityColor(
                              entity.sensitivity_level
                            )}
                            width={110}
                            sx={{ ml: 1 }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <StandardizedChip
                          label={entity.anonymization_method}
                          color={getMethodColor(entity.anonymization_method)}
                          width={110}
                        />
                      </TableCell>
                      <TableCell>
                        {entity.examples?.join(', ') || 'N/A'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Domains Tab */}
      {activeTab === 2 && (
        <Grid container spacing={3}>
          {entityData.domains?.map((domain, index) => (
            <Grid size={{ xs: 12, md: 6 }} key={index}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={2}>
                    <DomainIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6">{domain.display_name}</Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    {domain.description}
                  </Typography>
                  <Box mb={2}>
                    <Typography variant="subtitle2" gutterBottom>
                      Use Cases:
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={0.75}>
                      {domain.use_cases?.map((useCase, idx) => (
                        <StandardizedChip
                          key={idx}
                          label={useCase}
                          color="primary"
                        />
                      ))}
                    </Box>
                  </Box>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="body2" color="text.secondary">
                      {domain.entity_count} entities
                    </Typography>
                    <StandardizedChip
                      label={domain.domain_name.toUpperCase()}
                      color="primary"
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Anonymization Methods Tab */}
      {activeTab === 3 && (
        <Grid container spacing={3}>
          {entityData.anonymization_methods?.map((method, index) => (
            <Grid size={{ xs: 12, md: 6 }} key={index}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={2}>
                    <LockIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6">{method.display_name}</Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    {method.description}
                  </Typography>
                  <Box mb={2}>
                    <Typography variant="subtitle2" gutterBottom>
                      Security Level:
                    </Typography>
                    <StandardizedChip
                      label={method.security_level}
                      color={getSensitivityColor(method.security_level)}
                    />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Use Cases:
                    </Typography>
                    <List dense>
                      {method.use_cases?.map((useCase, idx) => (
                        <ListItem key={idx} sx={{ py: 0 }}>
                          <ListItemIcon>
                            <CheckIcon color="success" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary={useCase} />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Refresh Button */}
      <Box mt={3} display="flex" justifyContent="center">
        <Button
          variant="outlined"
          onClick={fetchEntityInformation}
          disabled={loading}
        >
          Refresh Information
        </Button>
      </Box>
    </Box>
  );
};

export default EntityInformation;
